import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment/moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { supplierApi } from 'api/supplier';
import { dealsApi } from 'api/deals';
import { leadsApi } from 'api/leads';
import { brandApi } from 'api/brand';
import { clientApi } from 'api/client';
import { itemApi } from 'api/item';
import { countryApi } from 'api/country';
import { purchaseOrderApi } from 'api/purchaseOrder';

export const taskApi = createApi({
  tagTypes: ['TASK', 'TASK_LIST', 'STATUS_ANALYTICS'],
  reducerPath: 'taskApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    taskCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/task',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL']));
          dispatch(leadsApi.util.invalidateTags(['LEAD']));
          dispatch(brandApi.util.invalidateTags(['BRAND']));
          dispatch(itemApi.util.invalidateTags(['ITEM']));
          dispatch(countryApi.util.invalidateTags(['COUNTRY']));
          dispatch(clientApi.util.invalidateTags(['CLIENT']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: () => ['TASK_LIST', 'STATUS_ANALYTICS'],
    }),
    taskUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/task',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL']));
          dispatch(leadsApi.util.invalidateTags(['LEAD']));
          dispatch(brandApi.util.invalidateTags(['BRAND']));
          dispatch(itemApi.util.invalidateTags(['ITEM']));
          dispatch(countryApi.util.invalidateTags(['COUNTRY']));
          dispatch(clientApi.util.invalidateTags(['CLIENT']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: () => ['TASK_LIST', 'TASK'],
    }),
    taskDetailsUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/task/details',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL']));
          dispatch(leadsApi.util.invalidateTags(['LEAD']));
          dispatch(brandApi.util.invalidateTags(['BRAND']));
          dispatch(itemApi.util.invalidateTags(['ITEM']));
          dispatch(countryApi.util.invalidateTags(['COUNTRY']));
          dispatch(clientApi.util.invalidateTags(['CLIENT']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: () => ['TASK_LIST', 'TASK'],
    }),
    taskStatusChange: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/task/status',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL']));
          dispatch(leadsApi.util.invalidateTags(['LEAD']));
          dispatch(brandApi.util.invalidateTags(['BRAND']));
          dispatch(itemApi.util.invalidateTags(['ITEM']));
          dispatch(countryApi.util.invalidateTags(['COUNTRY']));
          dispatch(clientApi.util.invalidateTags(['CLIENT']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: () => ['TASK_LIST', 'TASK', 'STATUS_ANALYTICS'],
    }),
    taskById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/task/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const item = response.data.taskResponses[0] || {};
        const managerMapping = response?.data?.managers || {};
        return {
          ...item,
          _assignedTo: managerMapping[item.assignedTo],
          _createdBy: managerMapping[item.createdBy],
          formattedLastModifiedDate: moment(item.lastModifiedDate).format('DD.MM.YY HH:mm:ss'),
          formattedCreateDate: moment(item.createDate).format('DD.MM.YY HH:mm:ss'),
          formattedDueDate: moment(item.dueDate).format('DD.MM.YY HH:mm:ss'),
        };
      },
      providesTags: () => ['TASK'],
    }),
    taskDelete: builder.mutation<any, any>({
      query: (id: string) => ({
        url: `/wrd/api/task/${id}`,
        method: 'delete',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL']));
          dispatch(leadsApi.util.invalidateTags(['LEAD']));
          dispatch(brandApi.util.invalidateTags(['BRAND']));
          dispatch(itemApi.util.invalidateTags(['ITEM']));
          dispatch(countryApi.util.invalidateTags(['COUNTRY']));
          dispatch(clientApi.util.invalidateTags(['CLIENT']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: () => ['TASK_LIST', 'STATUS_ANALYTICS'],
    }),
    tasksList: builder.query<any, any>({
      query: (args) => {
        const {
          size = 10,
          page = 1,
          entityType,
          forDashboard,
          entityId,
          isMainTable,
          sortField,
          sortOrder,
          search,
          filters,
        } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = {
          ...filters,
          ...(typeof search !== 'undefined' && { search }),
          ...(entityType !== undefined && { entityType }),
          ...(entityId !== undefined && { entityId }),
          ...(isMainTable !== undefined && { isMainTable }),
        };
        if (forDashboard) {
          params.forDashboard = true;
          params.assignedTo = JSON.parse(localStorage.getItem('userId') || '');
        }
        return {
          url: `/wrd/api/task/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const managerMapping = response?.data?.content?.[0]?.managers || {};
        response.data.content = (response?.data?.content?.[0]?.taskResponses || []).map((item: any) => ({
          ...item,
          _assignedTo: managerMapping[item.assignedTo],
          _createdBy: managerMapping[item.createdBy],
          formattedLastModifiedDate: moment(item.lastModifiedDate).format('DD.MM.YY HH:mm:ss'),
          formattedCreateDate: moment(item.createDate).format('DD.MM.YY HH:mm:ss'),
          formattedDueDate: moment(item.dueDate).format('DD.MM.YY HH:mm:ss'),
          key: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['TASK_LIST'],
    }),
    dashboardTasksList: builder.query<any, any>({
      query: (args) => {
        const { size = 100, page = 0, userId } = args;
        return {
          url: `/wrd/api/task/list?size=${size}&page=${page}&sort=priorityLevel,asc&assignedTo=${userId}&isMainTable=true&sortByStatus=true`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const managerMapping = response?.data?.content?.[0]?.managers || {};
        const priorityOrder: Record<string, number> = {
          HIGH: 1,
          STANDARD: 2,
          LOW: 3,
        };
        response.data.content = (response?.data?.content?.[0]?.taskResponses || [])
          .map((item: any) => ({
            ...item,
            _assignedTo: managerMapping[item.assignedTo],
            _createdBy: managerMapping[item.createdBy],
            formattedLastModifiedDate: moment(item.lastModifiedDate).format('DD.MM.YY HH:mm:ss'),
            formattedCreateDate: moment(item.createDate).format('DD.MM.YY HH:mm:ss'),
            formattedDueDate: moment(item.dueDate).format('DD.MM.YY HH:mm:ss'),
            key: item.id,
          }))
          .sort((a: any, b: any) => {
            return priorityOrder[a.priorityLevel] - priorityOrder[b.priorityLevel];
          });
        return response.data;
      },
      providesTags: () => ['TASK_LIST'],
    }),
    taskSelectList: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/task/selector-list`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['TASK_LIST'],
    }),
    taskStatusAnalytics: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/task/status-count`,
        };
      },
      transformResponse: (response: any) => {
        return response;
      },
      providesTags: () => ['STATUS_ANALYTICS'],
    }),
  }),
});

export const {
  useTaskSelectListQuery,
  useTasksListQuery,
  useTaskDeleteMutation,
  useTaskCreateMutation,
  useTaskUpdateMutation,
  useTaskDetailsUpdateMutation,
  useTaskByIdQuery,
  useLazyTaskByIdQuery,
  useTaskStatusChangeMutation,
  useTaskStatusAnalyticsQuery,
  useDashboardTasksListQuery,
} = taskApi;
